import React, { useState, useEffect } from "react";
import { Button } from "flowbite-react";
import { motion } from "framer-motion";
import { Link } from 'react-router-dom';
import { useDarkMode } from '../contexts/DarkModeContext';
import { subscribeToWaitlist } from '../services/newsletterService';

export default function LandingPage() {
  const { isDarkMode } = useDarkMode();
  const [email, setEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState({
    loading: false,
    success: false,
    error: null
  });

  // Function to scroll to newsletter section
  const scrollToNewsletter = () => {
    const newsletterSection = document.getElementById('newsletter');
    if (newsletterSection) {
      newsletterSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Handle newsletter subscription
  const handleNewsletterSubscribe = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setSubscribeStatus({
        loading: false,
        success: false,
        error: 'Please enter your email address'
      });
      return;
    }

    setSubscribeStatus({
      loading: true,
      success: false,
      error: null
    });

    try {
      // First try the Cloud Function approach
      let result = await subscribeToWaitlist(email);
      
      // If Cloud Function fails, try direct API approach as fallback
      if (!result.success) {
        console.log("Cloud function failed, trying direct API approach");
        result = await subscribeToWaitlist(email);
      }
      
      if (result.success) {
        setSubscribeStatus({
          loading: false,
          success: true,
          error: null
        });
        setEmail('');
      } else {
        setSubscribeStatus({
          loading: false,
          success: false,
          error: result.error || 'Failed to subscribe. Please try again.'
        });
      }
    } catch (error) {
      console.error("Newsletter subscription error:", error);
      setSubscribeStatus({
        loading: false,
        success: false,
        error: error.message || 'An unexpected error occurred.'
      });
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0A0A] text-gray-900 dark:text-white font-sans transition-colors duration-500">
      {/* Hero Section with herobg.png background */}
      <section className="relative overflow-hidden flex flex-col items-center justify-center text-center px-6 pt-40 md:pt-[15rem] pb-32">
        <div className="absolute inset-0 z-0">
          {/* Background image */}
          <div 
            className="absolute inset-0 bg-cover bg-center" 
            style={{ 
              backgroundImage: 'url("/images/herobg.png")',
              backgroundPosition: 'center center',
              height: '100%',
              width: '100%'
            }}
          ></div>
          
          {/* Removed transparent gradient overlay */}
          
          {/* Subtle accent color overlay */}
          <div className={`absolute inset-0 ${
            isDarkMode 
              ? 'bg-gradient-to-br from-emerald-900/5 via-transparent to-emerald-900/5' 
              : 'bg-gradient-to-br from-emerald-50/20 via-transparent to-emerald-50/20'
          }`}></div>
        </div>
        
        <div className="relative z-10 max-w-3xl">
          <h1 className="text-5xl md:text-7xl font-serif font-semibold tracking-tight leading-tight mb-6 text-white">
            Faith.<span className="text-white"> Connected.</span>
          </h1>
          <p className="text-xl text-white mb-10 max-w-2xl mx-auto">
            Your prayers, Quran, and Dhikr — beautifully designed and within reach. A premium Islamic experience for the modern Muslim.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button onClick={scrollToNewsletter} className="w-full sm:w-auto">
              <Button color="success" className="text-white bg-emerald-900 hover:bg-emerald-800 dark:bg-emerald-800 dark:hover:bg-emerald-700 text-base px-7 py-3 rounded-full shadow-md hover:shadow-emerald-200/40 dark:hover:shadow-emerald-800/20 transition-all duration-300 w-full sm:w-auto">
                Get Notified
              </Button>
            </button>
            <a href="#features" className="w-full sm:w-auto">
              <Button color="light" className="text-emerald-900 dark:text-emerald-300 bg-emerald-100/70 dark:bg-emerald-900/50 hover:bg-emerald-100 dark:hover:bg-emerald-800/70 text-base px-6 py-3 rounded-full transition-all duration-300 w-full sm:w-auto">
                Learn More
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Product Highlights */}
      <section id="features" className="relative">
        {/* Mobile App Section with landingbg1.png background */}
        <div className="relative py-32">
          {/* Background image for this section only */}
          <div className="absolute inset-0 z-0">
            <div 
              className="absolute inset-0 bg-cover bg-center" 
              style={{ 
                backgroundImage: 'url("/images/landingbg1.png")',
                backgroundPosition: 'center center',
              }}
            ></div>
            
            {/* Overlay with solid top transitioning to semi-transparent */}
            <div className="absolute inset-0" 
                style={{ 
                  background: isDarkMode 
                    ? 'linear-gradient(to bottom, #0A0A0A 5%, rgba(10, 10, 10, 0.65) 15%)' 
                    : 'linear-gradient(to bottom, white 5%, rgba(255, 255, 255, 0.65) 15%)'
                }}>
            </div>
          </div>
          
          <div className="container mx-auto px-6 grid md:grid-cols-2 gap-20 items-center relative z-10">
            <div className="w-full md:max-w-md mx-auto">
              <img 
                src="/images/iphone.png" 
                alt="Mobile App" 
                className="mx-auto w-full h-auto" 
              />
            </div>

            <div>
              <h2 className="text-4xl md:text-5xl font-serif font-semibold mb-4 text-emerald-900 dark:text-white">Ummahti<br/>Mobile App</h2>
              <p className="text-lg md:text-xl text-gray-800 dark:text-gray-300 mb-8 max-w-lg">
                A comprehensive toolkit and spiritual companion for your daily worship. Built for convenience on the go.
              </p>
              <ul className="space-y-3 mb-8">
                {["Precise prayer times with custom methods", "Quran with translations and audio", "Elegant dark & light themes"].map((feature, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-emerald-700 dark:text-emerald-400 mt-1 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-800 dark:text-gray-300">{feature}</span>
                  </li>
                ))}
              </ul>
              <Link to="/about">
                <Button color="success" className="rounded-full bg-emerald-800 hover:bg-emerald-900 border-0 text-white px-6 py-3">
                  Coming Soon
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Smart Adhan Clock Section with split background */}
        <div className="relative">
          <div className="grid md:grid-cols-2 items-stretch">
            {/* Left side with solid background */}
            <div className="bg-amber-50 dark:bg-[#0F1A14] py-32 px-6 md:px-12 lg:px-20 flex items-center">
              <div className="max-w-xl">
                <h2 className="text-4xl md:text-5xl font-serif font-semibold mb-4 text-emerald-900 dark:text-white">Ummahti<br/>Smart Adhan Clock</h2>
                <p className="text-lg md:text-xl text-gray-800 dark:text-gray-300 mb-8 max-w-lg">
                  Always accurate prayer times, beautifully designed and within reach. A premium Islamic experience for the modern Muslim.
                </p>
                <ul className="space-y-3 mb-8">
                  {["Seamlessly controlled via mobile app", "Personalize with beautiful themes and your own photos", "Multiple adhan options"].map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-emerald-700 dark:text-emerald-400 mt-1 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-800 dark:text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/signup">
                  <Button color="success" className="rounded-full bg-emerald-800 hover:bg-emerald-900 border-0 text-white px-6 py-3">
                    Join Waitlist
                  </Button>
                </Link>
              </div>
            </div>
            
            {/* Right side with background image */}
            <div className="relative">
              {/* Background image */}
              <div 
                className="absolute inset-0 bg-cover bg-center" 
                style={{ 
                  backgroundImage: 'url("/images/landingbg3.png")',
                  backgroundPosition: 'center center',
                }}
              ></div>
              
              {/* Semi-transparent overlay */}
              <div className="absolute inset-0 bg-amber-50/40 dark:bg-black/40"></div>
              
              {/* Content */}
              <div className="relative z-10 h-full flex items-center justify-center py-32 px-6">
                <div>
                  <img 
                    src="/images/mockup_clock.png" 
                    alt="Smart Adhan Clock" 
                    className="max-w-full h-auto drop-shadow-2xl"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* TV App Section with split background */}
        <div className="relative">
          <div className="grid md:grid-cols-2 items-stretch">
            {/* Left side - removed background image and overlay */}
            <div className="relative bg-white dark:bg-[#0A0A0A]">
              {/* Content */}
              <div className="relative z-10 h-full flex items-center justify-center py-32 px-6">
                <div>
                  <img 
                    src="/images/mockup_tv.png" 
                    alt="TV App" 
                    className="max-w-full h-auto drop-shadow-2xl"
                  />
                </div>
              </div>
            </div>
            
            {/* Right side with solid background */}
            <div className="bg-white dark:bg-[#0A0A0A] py-32 px-6 md:px-12 lg:px-20 flex items-center">
              <div className="max-w-xl">
                <h2 className="text-4xl md:text-5xl font-serif font-semibold mb-4 text-emerald-900 dark:text-white">Ummahti<br/>Google TV App</h2>
                <p className="text-lg md:text-xl text-gray-800 dark:text-gray-300 mb-8 max-w-lg">
                  Turn your Google TV into a beautiful prayer display for your home. Designed exclusively for Google TV devices.
                </p>
                <ul className="space-y-3 mb-8">
                  {["Multiple display themes", "Beautiful prayer time visuals", "Quran recitation with on-screen text"].map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-emerald-700 dark:text-emerald-400 mt-1 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-800 dark:text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/signup">
                  <Button color="success" className="rounded-full bg-emerald-800 hover:bg-emerald-900 border-0 text-white px-6 py-3">
                    Request Beta
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - A New Era for Muslim Technology */}
      <section id="newsletter" className="bg-gradient-to-b from-amber-50 to-white dark:from-[#0F1A14] dark:to-[#0A0A0A] py-24 relative">
        {/* Background pattern using widebgpattern.png */}
        <div className="absolute inset-0 z-0 pointer-events-none">
          {/* Dark mode pattern */}
          <div className={`absolute inset-0 ${!isDarkMode && 'hidden'}`}
               style={{
                 backgroundImage: 'url("/images/widebgpattern.png")',
                 backgroundSize: 'cover',
                 backgroundAttachment: 'fixed',
                 opacity: 0.07,
                 mixBlendMode: 'lighten'
               }}>
          </div>
          
          {/* Light mode pattern with inversion technique */}
          <div className={`absolute inset-0 ${isDarkMode && 'hidden'}`}
               style={{
                 backgroundImage: 'url("/images/widebgpattern.png")',
                 backgroundSize: 'cover',
                 backgroundAttachment: 'fixed',
                 opacity: 0.07,
                 filter: 'invert(1)',
                 mixBlendMode: 'multiply'
               }}>
          </div>
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold mb-6 md:mb-8 text-emerald-900 dark:text-white leading-tight">
              Be First in Line for Our <span className="text-emerald-700 dark:text-emerald-400">Smart Adhan Clock and Google TV App</span>
            </h2>
            <p className="text-lg md:text-xl text-gray-700 dark:text-gray-400 mb-8 md:mb-12 max-w-2xl mx-auto">
              Join our exclusive early access list and be among the first to experience our revolutionary Smart Adhan Clock and Google TV App. Get priority access, special launch discounts, and insider updates sent straight to your inbox.
            </p>
            
            <div className="bg-white dark:bg-[#121212] p-8 md:p-10 rounded-xl shadow-lg max-w-lg mx-auto">
              <h3 className="text-2xl font-medium mb-6 text-emerald-900 dark:text-white">
                {subscribeStatus.success ? 'Thank you for joining!' : 'Secure Your Early Access'}
              </h3>
              
              {subscribeStatus.success ? (
                <div className="text-center p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-emerald-600 dark:text-emerald-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <p className="text-emerald-800 dark:text-emerald-300">You're now on our priority list! We'll keep you updated on our launch and exclusive offers.</p>
                </div>
              ) : (
                <form onSubmit={handleNewsletterSubscribe} className="space-y-4">
                  <div className="flex flex-col sm:flex-row gap-4">
                    <input 
                      type="email" 
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Your email address" 
                      className="flex-1 border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#1A1A1A] rounded-lg py-3 px-4 text-base focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                    <Button 
                      type="submit"
                      color="success" 
                      className="text-white bg-emerald-800 hover:bg-emerald-900 dark:bg-emerald-700 dark:hover:bg-emerald-800 py-3 px-5 rounded-lg shadow-md"
                      disabled={subscribeStatus.loading}
                    >
                      {subscribeStatus.loading ? 'Subscribing...' : 'Get Early Access'}
                    </Button>
                  </div>
                  
                  {subscribeStatus.error && (
                    <p className="text-red-500 text-sm">{subscribeStatus.error}</p>
                  )}
                  
                  <p className="text-xs text-gray-500 mt-4">
                    By subscribing, you'll receive exclusive updates about our Smart Adhan Clock and Google TV App launch, plus special early-bird discounts. We respect your privacy and will never share your information.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
} 